import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import Sidebar from './Sidebar';
import HomeScreen from '../Home/HomeScreen';
import LibraryScreen from '../Library/LibraryScreen';
import LeadsScreen from '../Leads/LeadsScreen';
import DealRoomScreen from '../DealRoom/DealRoomScreen';
import DemoHubScreen from '../DemoHub/DemoHubScreen';
import AnalyticsScreen from '../Analytics/AnalyticsScreen';
import TrackingScreen from '../Tracking/TrackingScreen';
import BuyerView from '../Client/BuyerView';
import TeamScreen from '../Team/TeamScreen';
import MeetingsScreen from '../Meetings/MeetingsScreen';
import Toast from '../shared/Toast';
import type { Screen } from '../../types';

const SCREEN_MAP: Partial<Record<Screen, React.FC>> = {
  home:      HomeScreen,
  admin:     LibraryScreen,
  leads:     LeadsScreen,
  meetings:  MeetingsScreen,
  dealrooms: DealRoomScreen,
  demohub:   DemoHubScreen,
  analytics: AnalyticsScreen,
  tracking:  TrackingScreen,
  client:    BuyerView,
  team:      TeamScreen,
};

const SCREEN_LABELS: Partial<Record<Screen, { label: string; icon: string }>> = {
  home:      { label: 'Home',        icon: 'ti-home-2'              },
  admin:     { label: 'Library',     icon: 'ti-layout-grid'         },
  leads:     { label: 'Leads',       icon: 'ti-user-bolt'           },
  meetings:  { label: 'Meetings',    icon: 'ti-calendar-event'      },
  dealrooms: { label: 'Deal Room',   icon: 'ti-building'            },
  demohub:   { label: 'Demo Hub',    icon: 'ti-presentation-analytics' },
  analytics: { label: 'Analytics',   icon: 'ti-chart-bar'           },
  tracking:  { label: 'Tracking',    icon: 'ti-broadcast'           },
  client:    { label: 'Buyer View',  icon: 'ti-eye'                 },
  team:      { label: 'Team',        icon: 'ti-users'               },
};

export default function AppShell() {
  const { screen, currentUser } = useApp();
  const { isMobile, isTablet } = useBreakpoint();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const ScreenComponent = SCREEN_MAP[screen] || LibraryScreen;
  const screenMeta = SCREEN_LABELS[screen];

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar
        forceCollapsed={isTablet}
        isMobile={isMobile}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        overflow: 'hidden', minWidth: 0,
        background: 'var(--surface)',
      }}>
        {/* ── Mobile top bar ── */}
        {isMobile && (
          <div style={{
            height: '54px', flexShrink: 0,
            display: 'flex', alignItems: 'center',
            padding: '0 14px', gap: '10px',
            background: 'var(--navy)',
            borderBottom: '1px solid rgba(255,255,255,.07)',
          }}>
            {/* Hamburger */}
            <button
              type="button"
              title="Open navigation"
              onClick={() => setSidebarOpen(true)}
              style={{
                background: 'rgba(255,255,255,.08)',
                border: '1px solid rgba(255,255,255,.1)',
                borderRadius: '8px',
                color: 'rgba(255,255,255,.8)',
                cursor: 'pointer',
                fontSize: '16px',
                width: '34px', height: '34px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                transition: 'background .15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.14)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,.08)')}
            >
              <i className="ti ti-menu-2" />
            </button>

            {/* Screen title */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '7px' }}>
              {screenMeta && (
                <i className={`ti ${screenMeta.icon}`} style={{ fontSize: '14px', color: 'var(--gold2)', opacity: .85 }} />
              )}
              <span style={{ fontSize: '14px', fontWeight: '700', color: 'rgba(255,255,255,.92)', letterSpacing: '-.1px' }}>
                {screenMeta?.label ?? 'SalesBook'}
              </span>
            </div>

            {/* User avatar */}
            <div style={{
              width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
              background: currentUser?.color || 'var(--gold)',
              color: '#0C1929', fontSize: '11px', fontWeight: '700',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 0 2px rgba(255,255,255,.15)',
              cursor: 'pointer',
            }}>
              {currentUser?.ini}
            </div>
          </div>
        )}

        <ScreenComponent />
      </div>

      <Toast />
    </div>
  );
}
